import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, Mail, Lock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Auth = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // When a temp-password login succeeds, hold the user here until they set a new password
  const [pendingUser, setPendingUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const completeLogin = (user) => {
    // Clear any prior account's cached session/permissions before storing the new one
    localStorage.removeItem('currentUser');
    localStorage.removeItem('align_token');
    localStorage.removeItem('align31_alerts_read');
    localStorage.setItem('currentUser', JSON.stringify(user));
    localStorage.setItem('align_token', user.token);
    onLogin(user);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email: email.toLowerCase(),
        password: password
      });

      if (response.data.force_password_change) {
        setPendingUser(response.data);
        return;
      }
      completeLogin(response.data);
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        { current_password: password, new_password: newPassword },
        { headers: { Authorization: `Bearer ${pendingUser.token}` } }
      );
      completeLogin({ ...pendingUser, force_password_change: false });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="p-8 shadow-2xl">
          {/* Logo & Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-4">
              <Building2 className="h-8 w-8 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              ALIGN 3.1 Ops Manager
            </h1>
            <p className="text-sm text-muted-foreground">
              3.1 Supervised Living — Operations Dashboard
            </p>
          </div>

          {/* Login Form OR first-login password change */}
          {pendingUser ? (
            <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}
              <div className="p-3 rounded-md bg-primary/10 border border-primary/20 text-sm">
                Welcome, <span className="font-semibold">{pendingUser.name}</span>! Please set a new password to finish setting up your account.
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="password" value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10" required minLength={8} disabled={loading}
                    autoComplete="new-password" placeholder="At least 8 characters"
                    data-testid="new-password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Confirm New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="password" value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10" required minLength={8} disabled={loading}
                    autoComplete="new-password" placeholder="Repeat the new password"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="set-password-button">
                {loading ? 'Saving…' : 'Set Password & Continue'}
              </Button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md"
              >
                <AlertCircle className="h-4 w-4 text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </motion.div>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@myaligninc.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          )}

          {/* Footer */}
          <div className="mt-6 text-center text-xs text-muted-foreground">
            <p>ALIGN Inc. © 2026</p>
            <p className="mt-1">Authorized personnel only</p>
          </div>
        </Card>

        {/* Admin Info */}
        <div className="mt-4 text-center">
          <p className="text-xs text-muted-foreground">
            For access issues, contact your administrator
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Auth;
